<template>
	<RouterLink custom :to="to" v-if="to" v-slot="{ navigate }">
		<div @click="navigate" class="content-box link" :class="classes">
			<div class="content-box__content">
				<video autoplay loop muted playsinline id="site-header_video" class="content-box__video" v-if="video">
					<source :src="$src(video)" type="video/mp4">
				</video>
				<span class="content-box__slot">
					<slot></slot>
				</span>
			</div>
			<div class="content-box__link" v-if="to">
				<svg xmlns="http://www.w3.org/2000/svg" width="34" height="35" viewBox="0 0 34 35" fill="none">
					<line x1="16.5833" y1="0.5" x2="16.5833" y2="34.5" stroke="var(--plus-color)" stroke-width="2"/>
					<line y1="17.9166" x2="34" y2="17.9166" stroke="var(--plus-color)" stroke-width="2"/>
				</svg>
			</div>
		</div>
	</RouterLink>
	<a class="content-box link" :class="classes" v-else-if="external" :href="external" target="_blank">
		<div class="content-box__content">
			<video autoplay loop muted playsinline id="site-header_video" class="content-box__video" v-if="video">
				<source :src="$src(video)" type="video/mp4">
			</video>
			<span class="content-box__slot">
				<slot></slot>
			</span>
		</div>
		<div class="content-box__link" v-if="external">
			<svg xmlns="http://www.w3.org/2000/svg" width="34" height="35" viewBox="0 0 34 35" fill="none">
				<line x1="16.5833" y1="0.5" x2="16.5833" y2="34.5" stroke="var(--plus-color)" stroke-width="2"/>
				<line y1="17.9166" x2="34" y2="17.9166" stroke="var(--plus-color)" stroke-width="2"/>
			</svg>
		</div>
	</a>
	<div class="content-box" :class="classes" v-else>
		<div class="content-box__content">
			<video autoplay loop muted playsinline id="site-header_video" class="content-box__video" v-if="video">
				<source :src="$src(video)" type="video/mp4">
			</video>
			<span class="content-box__slot">
				<slot></slot>
			</span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ContentBox',
	props: {
		width: {
			type: [Number,String],
			default: 1,
			validator(v) {
				return [1,2,'1','2'].includes(v)
			},
		},
		height: {
			type: [Number,String],
			default: 1,
			validator(v) {
				return [1,2,'1','2'].includes(v)
			},
		},
		to: {
			type: Object,
			default: null,
		},
		video: {
			type: String,
			default: null,
		},
		external: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			let classes = []
			classes.push(`content-box--${this.width}x${this.height}`)
			if (this.to || this.external) {
				classes.push(`content-box--has-link`)
			}
			if (this.video) {
				classes.push(`content-box--has-video`)
			}

			return classes
		},
	},
	methods: {

	}
}
</script>

<style lang="scss" scoped>
.content-box {
	$box: &;

	--plus-size: 34px;
	--plus-color: var(--color--cream);

	@include modules.media-query('phone') {
		--border-radius: var(--border-radius--phone);
		min-height: 20vh;
	}

	display: block;
	overflow: hidden;

	@include modules.media-query('phone') {
		--plus-size: 18px;
	}

	&.dark {
		--plus-color: var(--color--dark-green);
	}

	overflow: hidden;
	position: relative;

	&.hidden {
		display: none;
		opacity: 0;
	}

	&--has-link {
		cursor: pointer;

		.content-box__slot {
			position: relative;

			> * {
				z-index: 2;
			}

			&:after {
				background-image: linear-gradient(0deg, rgba(var(--hover), 0.5) 0%, rgba(var(--hover), 0.2) 100%);
				content: '';
				display: inline-block;
				position: absolute;
				left: 0;
				top: 0;
				right: 0;
				bottom: 0;
				opacity: 0;
				transition: opacity 250ms linear;
				will-change: opacity;
				z-index: 1;
			}
		}

		&:hover {
			.content-box__slot {
				&:after {
					opacity: 1;
				}
			}
		}

	}

	&--2x2 {
		@include modules.ratio('square');
		grid-column: span 2;
		grid-row: span 2;

		&#{$box}--has-link {
			#{$box}__content {
				clip-path: url('#squircle-2x2');
			}
		}

	}

	&--2x1 {
		@include modules.ratio('custom', (0.47));
		grid-column: span 2;

		&#{$box}--has-link {
			#{$box}__content {
				clip-path: url('#squircle-2x1');
			}
		}
	}

	&--1x1 {
		@include modules.ratio('square');

		&#{$box}--has-link {
			#{$box}__content {
				clip-path: url('#squircle-1x1');
			}
		}

	}

	&--2x1--mobile {
		@include modules.media-query('phone') {
			@include modules.ratio('custom', (0.47));
			grid-column: span 2;

			&#{$box}--has-link {
				#{$box}__content {
					clip-path: url('#squircle-2x1');
				}
			}
		}
	}

	&__content {
		flex-direction: column;
		align-items: flex-start;
		background-color: rgba(var(--root--color--hunter-green), 0.8);
		border-radius: var(--border-radius);
		background-position: 50%;
		background-size: cover;
		background-repeat: no-repeat;
		background-image: var(--bg);
		bottom: 0;
		color: white;
		display: flex;
		justify-content: flex-end;
		left: 0;
		overflow: hidden;
		position: absolute;
		right: 0;
		text-align: center;
		top: 0;
		// padding: var(--border-radius);

		.content-box--has-video & {
			overflow: hidden;
		}

		& > * {
			z-index: 5;
		}

		@include modules.media-query('phone') {
			//padding: 1.25rem 1.25rem 1.25rem 1rem;
		}

		.chart & {
			justify-content: center;
		}

		.page__more & {
			// padding: 2rem 3rem 2rem 2rem;
		}

		.transparent &,
		.slash & {
			background-color: transparent;
			padding: 0;
			overflow: visible;
		}

		.green & {
			background-color: var(--color--hunter-green);
		}

		.leadership & {
			background-color: var(--color--leadership);
		}

		.value & {
			background-color: var(--color--value);
		}

		.insights & {
			background-color: var(--color--insights);
		}

		.responsibility & {
			background-color: var(--color--responsibility);
		}

		:deep(.page__link) {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			flex: 1 0 auto;

			font-family: var(--font-family--label);
			font-size: var(--font-author--size);
			line-height: var(--font-author--height);
			font-weight: var(--font-author--weight);
			text-transform: uppercase;

			@include modules.media-query('phone') {
				font-size: var(--font-author--size--mobile);
			}
		}

		:deep(.page__link--back) {
			color: var(--color--cream);
		}

		:deep(.page__link--forward) {
			color: var(--color--dark-green);

			.page__hint-icon {
				border: 1px solid var(--color--dark-green);
			}
		}
	}

	&__slot {
		display: flex;
		flex-direction: column;
		height: 100%;
		justify-content: flex-end;
		padding: var(--border-radius);
		padding: clamp(1rem, 3.77vw, var(--border-radius));
		position: relative;
		text-align: left;
		width: 100%;
		z-index: 5;

		@include modules.media-query('phone') {
			padding: clamp(0.5rem, 3.55vw, 1.5rem);
		}

		.slash &,
		.slide & {
			justify-content: flex-start;
			padding: 0;
		}

		.chart & {
			justify-content: space-between;
			// padding: 1.5rem;
			padding: clamp(1rem, 2.1vw, 1.5rem);
			@include modules.media-query('phone') {
				// padding: clamp(1rem, 2.1vw, 1.5rem);
			}

		}

		.content-box--has-video & {
			background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%);
		}

		.page__more & {
			padding: calc(var(--border-radius) / 1.5) calc(var(--border-radius) / .6) calc(var(--border-radius) / 1.5) calc(var(--border-radius) / 1.5);
		}

		:deep(.title--4) {
			line-height: 1;
			margin-bottom: .5rem;

			@include modules.media-query('tablet') {
				font-size: 14px;
			}
		}

	}

	&__link {
		position: absolute;
		left: auto;
		top: auto;
		right: 0;
		bottom: 0;

		svg {
			height: var(--plus-size);
			width: var(--plus-size);
			transition: transform 150ms linear;
			transform-origin: right bottom;
			will-change: transform;
		}
	}

	&__video {
		position: absolute;
		left: 50%;
		top: 50%;
		min-width: 100%;
		min-height: 100%;
		width: 100%;
		height: auto;
		transform: translate(-50%, -50%);
		z-index: 1;
		object-fit: cover;
		margin-left: calc(0 - var(--border-radius));
	}

	&:hover {
		.content-box__link {
			svg {
				transform: scale(1.4);
			}
		}
	}

	.body,
	.box-body,
	:deep(.body),
	:deep(.box-body) {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		padding: var(--border-radius);

		// .title {
		// 	text-align: left;

		// 	&--author {
		// 		font-family: var(--font-family--label);
		// 		font-size: var(--font-author--size);
		// 		line-height: var(--font-author--height);
		// 		font-weight: var(--font-author--weight);
		// 		margin-top: 2.5rem;
		// 		text-transform: uppercase;

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-author--size--mobile);
		// 		}

		// 		&--small {
		// 			font-size: var(--font-author--small--size);

		// 			@include modules.media-query('phone') {
		// 				font-size: var(--font-author--small--size--mobile);
		// 			}
		// 		}
		// 	}

		// 	span {
		// 		font-family: var(--font-family--serif);
		// 		font-size: var(--font-author-title--size);
		// 		display: block;
		// 		text-transform: none;

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-author-title--size--mobile);
		// 		}
		// 	}

		// 	&--1 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-1--size);
		// 		line-height: var(--font-tile-1--height);
		// 		font-weight: var(--font-tile-1--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-1--size--mobile);
		// 		}
		// 	}

		// 	&--2 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-2--size);
		// 		line-height: var(--font-tile-2--height);
		// 		font-weight: var(--font-tile-2--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-2--size--mobile);
		// 		}
		// 	}

		// 	&--3 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-3--size);
		// 		line-height: var(--font-tile-3--height);
		// 		font-weight: var(--font-tile-3--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-3--size--mobile);
		// 		}
		// 	}

		// 	&--4 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-4--size);
		// 		line-height: var(--font-tile-4--height);
		// 		font-weight: var(--font-tile-4--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-4--size--mobile);
		// 		}
		// 	}

		// 	&--5 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-5--size);
		// 		line-height: var(--font-tile-5--height);
		// 		font-weight: var(--font-tile-5--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-5--size--mobile);
		// 		}

		// 		small {
		// 			display: block;
		// 			font-weight: 200;
		// 		}
		// 	}

		// 	&--60 {
		// 		font-family: var(--font-family--body);
		// 		font-size: var(--font-tile-60--size);
		// 		line-height: var(--font-tile-60--height);
		// 		font-weight: var(--font-tile-60--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-60--size--mobile);
		// 		}
		// 	}

		// 	&--80 {
		// 		font-family: var(--font-family--sans);
		// 		font-size: var(--font-tile-80--size);
		// 		line-height: var(--font-tile-80--height);
		// 		font-weight: var(--font-tile-80--weight);

		// 		@include modules.media-query('phone') {
		// 			font-size: var(--font-tile-80--size--mobile);
		// 		}
		// 	}
		// }
	}
}
</style>
